import { ApiItemType, ApiListOptions, ApiSortDirection, ApiSortOption } from '@ibe/api';
import { itemListEvent, LoggerFactory } from '@ibe/services';
import { observer, useLocalObservable } from 'mobx-react';
import { runInAction } from 'mobx';
import React, { useCallback, useEffect, useRef } from 'react';
import { Button } from 'reactstrap';
import { CollapsibleRefFns } from '../../SubModules/SearchCollapsible/SearchCollapsible';
import fallback from '../../../_Translations/generated/package.en.t.json';
import Keys from '../../../_Translations/generated/package.en.t.json.keys';
import useApi from '../../Hooks/useApi';
import useConfig from '../../Hooks/useConfig';
import useTranslation from '../../Hooks/useTranslation';
import PackageParams from '../../SubModules/PackageDetails/PackageParams';
import PackageStore from './PackageStore';
import Props from './externals';
import Search from './Search';
import Details from './Details';
import List from './List';
import ErrorDisplay from './ErrorDisplay';
import useWindow from '../../Hooks/useWindow';
import { useMount } from 'react-use';
import useIsOnProductPage from '../../Hooks/useIsOnProductPage';

export enum PackageModuleState {
  LIST,
  DETAILS
}

export const PackageRenderer = observer(function PackageRenderer(
  props: Props & { store: PackageStore }
) {
  const { t } = useTranslation('package', fallback, Keys);

  const logger = LoggerFactory.get('Package.tsx');

  const {
    store,
    showTotalPriceOnly,
    subType = ApiItemType.PACKAGE,
    render,
    sorting,
    sortingType,
    renderListItem,
    doBestPriceSearch,
    bestPriceMaxDateRangeSpan,
    availabilitySortingActive,
    onBestPriceAvailabilitySelect,
    showLabels,
    destinationMandatory,
    endDateMandatory,
    showMealDetails,
    minSelectableChildAge,
    noDatePickerOverflow,
    showCharacteristics,
    stickySearch,
    changeOnBlur,
    onlyAdultSelect,
    bestPriceStickySearch,
    bestPriceDesktopSearchCollapse,
    bestPriceRenderSearchContent,
    bestPriceIsStickyListener,
    onBestPriceSearchChange,
    bestPriceStickyOffset,
    onPackagesLoaded,
    bestPriceSubmitOnChange,
    paginationProps,
    widgetClickable,
    bestPriceHideDetailBackButton,
    ExternalBestPriceDetailsContent,
    bestPriceListSelectionsMode,
    withRoomCountSelector,
    includeCheapestFlightInRoomPrice,
    splitRequiredComponentPricePerPerson,
    bestPriceAfterSearch,
    showPaxSelectionInModal,
    showDateSelectionInModal,
    modalLabel,
    bestPriceShowDirectFlightsOnlyFilter,
    customListOptions,
    showSearchParamsPerItem,
    singleSelectCategories,
    internalFlightLogo,
    showInternalFlightLogo,
    scrollOnInitialSearch,
    useGeoUnitTwoColumnStyling
  } = props;
  const { setIsOnProductPage } = useIsOnProductPage();
  const onSelect = props.onSelect || logger.warn;
  const onSearch = props.onSearch || logger.warn;
  const showMultipleDurations = props.showMultipleDurations !== false;
  const stateRef = useRef<PackageModuleState>(
    props.searchParams?.packageDetailId || props.searchParams?.goToDetails
      ? PackageModuleState.DETAILS
      : PackageModuleState.LIST
  );
  const handleOnDetail = (): void => {
    store.listClicked = true;
    stateRef.current = PackageModuleState.DETAILS;
    if (props.onDetail) {
      props.onDetail(store.searchParams, store.listOptions);
    }
  };

  const goBackToList = (): void => {
    if (stateRef.current === PackageModuleState.DETAILS) {
      const params = store.searchParams;
      store.resetDetail();
      params.packageDetailId = undefined;
      params.goToDetails = false;
      store.setSearchParams(params);
      onSearch(store.searchParams, store.listOptions);
      stateRef.current = PackageModuleState.LIST;
    }
  };

  useMount(() => {
    window.addEventListener('popstate', goBackToList);
    return () => {
      window.removeEventListener('popstate', goBackToList);
    };
  });

  useEffect(() => {
    setIsOnProductPage(store.hasDetail);
  }, [store.hasDetail]);

  if (render) {
    return render(
      <Search
        {...props}
        store={store}
        stickySearch={stickySearch && (store.hasList || store.hasDetail)}
        showDateSelectionInModal={showDateSelectionInModal}
        showPaxSelectionInModal={showPaxSelectionInModal}
        modalLabel={modalLabel}
      />,
      <ErrorDisplay store={store} />,
      <List
        store={store}
        onDetail={handleOnDetail}
        showMultipleDurations={showMultipleDurations}
        subType={subType}
        sorting={sorting}
        sortingType={sortingType}
        renderListItem={renderListItem}
        doBestPriceSearch={doBestPriceSearch}
        paginationProps={paginationProps}
        customListOptions={customListOptions}
        showSearchParamsPerItem={showSearchParamsPerItem}
        singleSelectCategories={singleSelectCategories}
      />,
      <Details
        store={store}
        onSelect={onSelect}
        showTotalPriceOnly={showTotalPriceOnly}
        goBackToList={goBackToList}
        bestPriceMaxDateRangeSpan={bestPriceMaxDateRangeSpan}
        showLabels={showLabels}
        destinationMandatory={destinationMandatory}
        endDateMandatory={endDateMandatory}
        availabilitySortingActive={availabilitySortingActive}
        onBestPriceAvailabilitySelect={(packageCartId?: string): void => {
          if (onBestPriceAvailabilitySelect) {
            onBestPriceAvailabilitySelect(
              store.getPackagesResponse,
              store.searchParams,
              packageCartId
            );
          }
        }}
        showMealDetails={showMealDetails}
        showCharacteristics={showCharacteristics}
        minSelectableChildAge={minSelectableChildAge}
        noDatePickerOverflow={noDatePickerOverflow}
        changeOnBlur={changeOnBlur}
        onlyAdultSelect={onlyAdultSelect}
        bestPriceStickySearch={bestPriceStickySearch}
        bestPriceRenderSearchContent={bestPriceRenderSearchContent}
        bestPriceDesktopSearchCollapse={bestPriceDesktopSearchCollapse}
        bestPriceIsStickyListener={bestPriceIsStickyListener}
        onBestPriceSearchChange={onBestPriceSearchChange}
        bestPriceStickyOffset={bestPriceStickyOffset}
        bestPriceSubmitOnChange={bestPriceSubmitOnChange}
        bestPriceHideDetailBackButton={bestPriceHideDetailBackButton}
        ExternalBestPriceDetailsContent={ExternalBestPriceDetailsContent}
        bestPriceListSelectionsMode={bestPriceListSelectionsMode}
        subType={subType}
        withRoomCountSelector={withRoomCountSelector}
        includeCheapestFlightInRoomPrice={includeCheapestFlightInRoomPrice}
        splitRequiredComponentPricePerPerson={splitRequiredComponentPricePerPerson}
        bestPriceAfterSearch={bestPriceAfterSearch}
        bestPriceShowDirectFlightsOnlyFilter={bestPriceShowDirectFlightsOnlyFilter}
        internalFlightLogo={internalFlightLogo}
        showInternalFlightLogo={showInternalFlightLogo}
        scrollOnInitialSearch={scrollOnInitialSearch}
      />,
      store
    );
  }

  return (
    <div className="iso__package__container">
      <div className="iso__package__container__inner mb-4">
        <div>
          <Search
            {...props}
            store={store}
            stickySearch={stickySearch && (store.hasList || store.hasDetail)}
          />
        </div>
        {(!stickySearch || (!store.hasList && !store.hasDetail)) && (
          <div>
            <ErrorDisplay store={store} />
          </div>
        )}
      </div>

      {!store.hasDetail && store.hasList && !onPackagesLoaded && (
        <div>
          <List
            store={store}
            onDetail={handleOnDetail}
            showMultipleDurations={showMultipleDurations}
            sorting={sorting}
            sortingType={sortingType}
            subType={subType}
            renderListItem={renderListItem}
            doBestPriceSearch={doBestPriceSearch}
            paginationProps={paginationProps}
            customListOptions={customListOptions}
            showSearchParamsPerItem={showSearchParamsPerItem}
            singleSelectCategories={singleSelectCategories}
          />
        </div>
      )}

      {store.hasDetail && (
        <>
          {!doBestPriceSearch && (
            <>
              <Button onClick={(): void => goBackToList()}> {t(Keys.BACK_TO_LIST)}</Button>
              <hr />
            </>
          )}
          <Details
            store={store}
            onSelect={onSelect}
            showTotalPriceOnly={showTotalPriceOnly}
            goBackToList={goBackToList}
            bestPriceMaxDateRangeSpan={bestPriceMaxDateRangeSpan}
            showLabels={showLabels}
            destinationMandatory={destinationMandatory}
            endDateMandatory={endDateMandatory}
            availabilitySortingActive={availabilitySortingActive}
            onBestPriceAvailabilitySelect={(packageCartId?: string): void => {
              if (onBestPriceAvailabilitySelect) {
                onBestPriceAvailabilitySelect(
                  store.getPackagesResponse,
                  store.searchParams,
                  packageCartId
                );
              }
            }}
            showMealDetails={showMealDetails}
            showCharacteristics={showCharacteristics}
            minSelectableChildAge={minSelectableChildAge}
            noDatePickerOverflow={noDatePickerOverflow}
            changeOnBlur={changeOnBlur}
            onlyAdultSelect={onlyAdultSelect}
            bestPriceStickySearch={bestPriceStickySearch}
            bestPriceRenderSearchContent={bestPriceRenderSearchContent}
            bestPriceDesktopSearchCollapse={bestPriceDesktopSearchCollapse}
            bestPriceIsStickyListener={bestPriceIsStickyListener}
            onBestPriceSearchChange={onBestPriceSearchChange}
            bestPriceStickyOffset={bestPriceStickyOffset}
            bestPriceSubmitOnChange={bestPriceSubmitOnChange}
            bestPriceHideDetailBackButton={bestPriceHideDetailBackButton}
            widgetClickable={widgetClickable}
            ExternalBestPriceDetailsContent={ExternalBestPriceDetailsContent}
            bestPriceListSelectionsMode={bestPriceListSelectionsMode}
            includeCheapestFlightInRoomPrice={includeCheapestFlightInRoomPrice}
            splitRequiredComponentPricePerPerson={splitRequiredComponentPricePerPerson}
            bestPriceAfterSearch={bestPriceAfterSearch}
            withRoomCountSelector={withRoomCountSelector}
            bestPriceShowDirectFlightsOnlyFilter={bestPriceShowDirectFlightsOnlyFilter}
            internalFlightLogo={internalFlightLogo}
            showInternalFlightLogo={showInternalFlightLogo}
            scrollOnInitialSearch={scrollOnInitialSearch}
            useGeoUnitTwoColumnStyling={useGeoUnitTwoColumnStyling}
          />
        </>
      )}
    </div>
  );
});

const logger = LoggerFactory.get('Package.tsx');

export const Package = observer(function Package(props: Props) {
  const window = useWindow();
  const api = useApi();
  const config = useConfig();
  const collapsibleRef = useRef<CollapsibleRefFns>(null);
  const initialRender = useRef<boolean>(false);
  const isManualSearch = useRef<boolean>(false);

  const {
    onSelect,
    onDetail,
    onSearch,
    searchParams,
    recoverMissingPackage = false,
    render,
    serviceSearchWithChildAgePicker,
    serviceSearchChildAgeRestriction,
    withModifySearch,
    showLabels,
    showMultipleDurations,
    showTotalPriceOnly,
    subType = ApiItemType.PACKAGE,
    listOptions,
    hideOrigin,
    destinationMandatory,
    endDateMandatory,
    withDateRangePicker,
    withAdvancedDateRangePicker,
    withGroupRequestForm,
    withRoomCountSelector,
    renderSearch,
    hideDestination,
    sorting,
    sortingType,
    minDateRangeSpan,
    maxDateRangeSpan,
    doBestPriceSearch,
    renderListItem,
    bestPriceMaxDateRangeSpan,
    availabilitySortingActive,
    onBestPriceAvailabilitySelect,
    showMealDetails,
    minSelectableChildAge,
    noDatePickerOverflow,
    selectLabelSort,
    renderSearchContent,
    desktopSearchCollapse,
    stickySearch,
    showCharacteristics,
    withChildrenSelection = true,
    changeOnBlur,
    onlyAdultSelect,
    bestPriceStickySearch,
    bestPriceRenderSearchContent,
    bestPriceDesktopSearchCollapse,
    bestPriceIsStickyListener,
    isStickyListener,
    onBestPriceSearchChange,
    bestPriceStickyOffset,
    packageOriginMustBeSet,
    packageOriginWithRemoveAll,
    packageOriginWithSelectAll,
    optionsMapper,
    renderDestinationLabel,
    customIndexedDestinationSelect,
    dateRangeDurationProps,
    onPackagesLoaded,
    externalGetPackagesResponse,
    bestPriceSubmitOnChange,
    bestPriceHideDetailBackButton,
    hideSearchInDetails,
    paginationProps,
    widgetClickable,
    elementsBeforePaxSelector,
    ExternalBestPriceDetailsContent,
    bestPriceListSelectionsMode,
    isInitialEventTrigger,
    blockInitialSearch,
    onInitialSearch,
    includeCheapestFlightInRoomPrice,
    splitRequiredComponentPricePerPerson,
    dateRangeUpdateStateFromProps,
    bestPriceAfterSearch,
    dateRangeClearSelectionsOnOpen,
    dateRangeFillWithLastDateRangeWhenNoSelection,
    showPaxSelectionInModal,
    showDateSelectionInModal,
    modalLabel,
    customSetIsLoading,
    bestPriceShowDirectFlightsOnlyFilter,
    customListOptions,
    showSearchParamsPerItem,
    customPackageSearchStore,
    paxSelectionRevalidateChildrenOnChangeAfterError,
    paxSelectionValidateChildrenPaxSelectionOnLeave,
    paxSelectionValidateChildrenPaxSelectionOnChange,
    paxSelectionUpdatePaxOnChangeWhenValid,
    paxSelectionUpdatePaxOnLeaveWhenValid,
    paxRoomSelectionImperativeHandleRef,
    onPackageSearchChange,
    singleSelectCategories,
    childAgePickerHeader,
    internalFlightLogo,
    showInternalFlightLogo,
    scrollOnInitialSearch,
    useGeoUnitTwoColumnStyling
  } = props;

  const defaultStore = useLocalObservable(
    () =>
      new PackageStore(
        api,
        config,
        doBestPriceSearch
          ? { sortDirection: ApiSortDirection.ASC, sortOption: ApiSortOption.PRICE }
          : undefined,
        customSetIsLoading
      )
  );
  const store = props.store || defaultStore;

  const onSearchDefault = async (packageParamsPartial: Partial<PackageParams>): Promise<void> => {
    const hasSelectedPackage = (): boolean =>
      store.list.some(item => item.id === store.searchParams.itemId);

    try {
      store.setIsLoading(true);
      store.setSearchParams(packageParamsPartial);
      if (listOptions) {
        store.setListOptions(listOptions as ApiListOptions);
      }
      if (!packageParamsPartial.itemId) {
        await store.loadPackages(subType, doBestPriceSearch);
        if (store.list?.length > 0 && packageParamsPartial.productId === undefined) {
          // This will trigger the event only once, to prevent it firing multiple times on remounts
          if (isInitialEventTrigger) {
            itemListEvent.broadcast({ items: store.list });
          }
          if (onPackagesLoaded) {
            onPackagesLoaded(store.getPackagesResponse, store.searchParams);
          }
        }
      }
      if (
        packageParamsPartial.itemId ||
        (packageParamsPartial.destinationItem && packageParamsPartial.goToDetails) ||
        (packageParamsPartial.mainServiceCodes &&
          packageParamsPartial.mainServiceCodes.length === 1 &&
          packageParamsPartial.goToDetails)
      ) {
        if (
          store.list?.length === 0 ||
          (recoverMissingPackage && !!packageParamsPartial.itemId && !hasSelectedPackage())
        ) {
          await store.loadPackages(subType, doBestPriceSearch);
        }
        if (
          (!recoverMissingPackage || !packageParamsPartial.itemId) &&
          (packageParamsPartial.destinationItem ||
            (packageParamsPartial.mainServiceCodes &&
              packageParamsPartial.mainServiceCodes.length === 1)) &&
          store.list?.length === 1
        ) {
          store.setSearchParams({ ...store.searchParams, itemId: store.list[0].id });
        }
        if (store.searchParams.itemId) {
          if (recoverMissingPackage) {
            if (!hasSelectedPackage()) {
              // Keep the results, but dont display a different package's stale details.
              runInAction(() => {
                store.resetDetail();
                store.setSearchParams({ ...store.searchParams, goToDetails: false });
                if (!store.error && !store.warning) {
                  store.error = Keys.apiNotAbleToGetPackages;
                }
              });
              return;
            }

            // Clear a previous unavailable-selection error after a valid selection
            if (store.error === Keys.apiNotAbleToGetPackages) {
              runInAction(() => {
                store.error = null;
              });
            }
          }
          await store.loadPackage().catch(reason => logger.error(reason));
        } else {
          store.resetDetail();
        }
      } else {
        store.resetDetail();
      }
    } catch (error) {
      logger.error(error);
    } finally {
      store.setIsLoading(false);
    }
  };

  const handleLoadPackageData = useCallback(async (): Promise<void> => {
    //Skip if searchParams only contains the dateInputType
    if (
      !(
        searchParams?.dateInputType &&
        ((Object.keys(searchParams).length === 3 &&
          !!searchParams.startDate &&
          !!searchParams.endDate) ||
          Object.keys(searchParams).length === 1)
      )
    ) {
      if (searchParams && Object.keys(searchParams).length > 0) {
        await onSearchDefault(searchParams);
        if (!config.outsideElementsContainerId) {
          window?.scrollTo({ top: 0, behavior: 'smooth' });
        }
        if (store.hasList) {
          collapsibleRef?.current?.closeCollapsible();
        }
      }
    }
    store.setIsLoading(false);
  }, [searchParams, config, store, collapsibleRef, recoverMissingPackage]);

  useEffect(() => {
    if (!initialRender.current || isManualSearch.current) {
      initialRender.current = true;
      isManualSearch.current = false;
      if (!blockInitialSearch) {
        handleLoadPackageData();
        if (onInitialSearch) {
          onInitialSearch(searchParams, listOptions);
        }
      }
    } else {
      initialRender.current = false;
    }
  }, [searchParams]);

  useEffect(() => {
    if (externalGetPackagesResponse) {
      store.getPackagesResponse = externalGetPackagesResponse;
      store.setListOptions(externalGetPackagesResponse.listOptions);
    }
  }, [externalGetPackagesResponse]);

  const handleSearch = (localParams: PackageParams, localListOptions: ApiListOptions): void => {
    isManualSearch.current = true;
    if (onSearch) {
      onSearch(localParams, localListOptions);
    }
  };

  const handleDetail = (localParams: PackageParams, localListOptions: ApiListOptions): void => {
    isManualSearch.current = true;
    if (onDetail) {
      onDetail(localParams, localListOptions);
    }
  };

  return (
    <PackageRenderer
      store={store}
      onSearch={onSearch ? handleSearch : onSearchDefault}
      onSelect={onSelect}
      onDetail={onDetail ? handleDetail : onSearchDefault}
      searchParams={searchParams}
      render={render}
      serviceSearchChildAgeRestriction={serviceSearchChildAgeRestriction}
      serviceSearchWithChildAgePicker={serviceSearchWithChildAgePicker}
      withModifySearch={withModifySearch}
      showLabels={showLabels}
      showTotalPriceOnly={showTotalPriceOnly}
      showMultipleDurations={showMultipleDurations}
      hideOrigin={hideOrigin}
      hideDestination={hideDestination}
      subType={subType}
      collapsibleRef={collapsibleRef}
      destinationMandatory={destinationMandatory}
      endDateMandatory={endDateMandatory}
      withDateRangePicker={withDateRangePicker}
      withAdvancedDateRangePicker={withAdvancedDateRangePicker}
      withGroupRequestForm={withGroupRequestForm}
      withRoomCountSelector={withRoomCountSelector}
      renderSearch={renderSearch}
      renderSearchContent={renderSearchContent}
      desktopSearchCollapse={desktopSearchCollapse}
      sorting={sorting}
      sortingType={sortingType}
      minDateRangeSpan={minDateRangeSpan}
      maxDateRangeSpan={maxDateRangeSpan}
      doBestPriceSearch={doBestPriceSearch}
      renderListItem={renderListItem}
      bestPriceMaxDateRangeSpan={bestPriceMaxDateRangeSpan}
      availabilitySortingActive={availabilitySortingActive}
      onBestPriceAvailabilitySelect={onBestPriceAvailabilitySelect}
      showMealDetails={showMealDetails}
      minSelectableChildAge={minSelectableChildAge}
      noDatePickerOverflow={noDatePickerOverflow}
      selectLabelSort={selectLabelSort}
      stickySearch={stickySearch}
      showCharacteristics={showCharacteristics}
      withChildrenSelection={withChildrenSelection}
      changeOnBlur={changeOnBlur}
      onlyAdultSelect={onlyAdultSelect}
      bestPriceStickySearch={bestPriceStickySearch}
      bestPriceRenderSearchContent={bestPriceRenderSearchContent}
      bestPriceDesktopSearchCollapse={bestPriceDesktopSearchCollapse}
      isStickyListener={isStickyListener}
      bestPriceIsStickyListener={bestPriceIsStickyListener}
      onBestPriceSearchChange={onBestPriceSearchChange}
      bestPriceStickyOffset={bestPriceStickyOffset}
      optionsMapper={optionsMapper}
      renderDestinationLabel={renderDestinationLabel}
      packageOriginMustBeSet={packageOriginMustBeSet}
      packageOriginWithRemoveAll={packageOriginWithRemoveAll}
      packageOriginWithSelectAll={packageOriginWithSelectAll}
      customIndexedDestinationSelect={customIndexedDestinationSelect}
      dateRangeDurationProps={dateRangeDurationProps}
      onPackagesLoaded={onPackagesLoaded}
      bestPriceSubmitOnChange={bestPriceSubmitOnChange}
      bestPriceHideDetailBackButton={bestPriceHideDetailBackButton}
      bestPriceShowDirectFlightsOnlyFilter={bestPriceShowDirectFlightsOnlyFilter}
      hideSearchInDetails={hideSearchInDetails}
      paginationProps={paginationProps}
      widgetClickable={widgetClickable}
      elementsBeforePaxSelector={elementsBeforePaxSelector}
      ExternalBestPriceDetailsContent={ExternalBestPriceDetailsContent}
      bestPriceListSelectionsMode={bestPriceListSelectionsMode}
      includeCheapestFlightInRoomPrice={includeCheapestFlightInRoomPrice}
      splitRequiredComponentPricePerPerson={splitRequiredComponentPricePerPerson}
      dateRangeUpdateStateFromProps={dateRangeUpdateStateFromProps}
      dateRangeFillWithLastDateRangeWhenNoSelection={dateRangeFillWithLastDateRangeWhenNoSelection}
      dateRangeClearSelectionsOnOpen={dateRangeClearSelectionsOnOpen}
      bestPriceAfterSearch={bestPriceAfterSearch}
      showPaxSelectionInModal={showPaxSelectionInModal}
      showDateSelectionInModal={showDateSelectionInModal}
      modalLabel={modalLabel}
      customListOptions={customListOptions}
      showSearchParamsPerItem={showSearchParamsPerItem}
      customPackageSearchStore={customPackageSearchStore}
      paxSelectionRevalidateChildrenOnChangeAfterError={
        paxSelectionRevalidateChildrenOnChangeAfterError
      }
      paxSelectionValidateChildrenPaxSelectionOnLeave={
        paxSelectionValidateChildrenPaxSelectionOnLeave
      }
      paxSelectionValidateChildrenPaxSelectionOnChange={
        paxSelectionValidateChildrenPaxSelectionOnChange
      }
      paxSelectionUpdatePaxOnChangeWhenValid={paxSelectionUpdatePaxOnChangeWhenValid}
      paxSelectionUpdatePaxOnLeaveWhenValid={paxSelectionUpdatePaxOnLeaveWhenValid}
      paxRoomSelectionImperativeHandleRef={paxRoomSelectionImperativeHandleRef}
      onPackageSearchChange={onPackageSearchChange}
      singleSelectCategories={singleSelectCategories}
      childAgePickerHeader={childAgePickerHeader}
      internalFlightLogo={internalFlightLogo}
      showInternalFlightLogo={showInternalFlightLogo}
      scrollOnInitialSearch={scrollOnInitialSearch}
      useGeoUnitTwoColumnStyling={useGeoUnitTwoColumnStyling}
    />
  );
});
